import { formatNumber } from "@ciq/shared/utils/format"

export const END_ZONE_BUILDING_FILL_COLOR = "#EEE"
export const DEFAULT_METRIC_FILL_COLOR = "#AAA"
export const DEFAULT_METRIC_OUTLINE_COLOR = "#888"

class UtilizationRange {
  constructor(min, max, title, color, options) {
    const modifier = options?.modifier != null ? options.modifier : 1
    const symbol = options?.symbol != null ? options.symbol : ""
    const symbolHasSpace = options?.symbolHasSpace != null ? options.symbolHasSpace : true

    this.id = `utilization-range-${min * modifier}-${max * modifier}`
    this.min = min
    this.max = max
    this.title = title

    let range = ""
    if (min != null && max != null) {
      range += `${formatNumber(Math.round(min * modifier))} - ${formatNumber(Math.round(max * modifier))}`
    } else if (min != null) {
      range += `${formatNumber(Math.round(min * modifier))} - ~`
    } else {
      range += `~ - ${formatNumber(Math.round(max * modifier))}${symbol}`
    }

    if (symbol) {
      range += symbolHasSpace ? ` ${symbol}` : symbol
    }

    this.range = range
    this.color = color
  }

  isInRange(value) {
    if (value == null) {
      return false
    }

    if (value < this.min) {
      return false
    }

    return this.max != null ? this.max >= value : true
  }
}

/**
 * Build UtilizationRange array from API-provided range definitions.
 *
 * @param {Object} column - The column configuration (has isPercentageMetric(), symbol)
 * @param {number} maxValue - Maximum value for scaling non-percentage metrics
 * @param {Object} colorRanges - Range definitions from API {percentage: [...], default: [...]}
 * @returns {UtilizationRange[]|null}
 * @throws {Error} If colorRanges is missing required data
 */
export const buildColorRanges = (column, maxValue, colorRanges) => {
  if (column == null || maxValue == null) {
    return null
  }

  if (!colorRanges) {
    throw new Error("API contract violation: colorRanges is required")
  }

  const rangeKey = column.isPercentageMetric() ? "percentage" : "default"
  const ranges = colorRanges[rangeKey]

  if (!ranges) {
    throw new Error(`API contract violation: missing colorRanges.${rangeKey}`)
  }

  if (column.isPercentageMetric()) {
    // Percentage metrics: use ranges as-is (0-1 scale, displayed as 0-100%)
    return ranges.map(
      r =>
        new UtilizationRange(r.min, r.max, r.label, r.color, {
          modifier: 100,
          symbol: "%",
          symbolHasSpace: false,
        })
    )
  }

  // Non-percentage metrics: scale ranges by maxValue
  return ranges.map(
    r =>
      new UtilizationRange(r.min * maxValue, r.max * maxValue, r.label, r.color, {
        symbol: column.symbol,
        symbolHasSpace: false,
      })
  )
}

export const getColor = (ranges, value) => {
  if (ranges == null || value == null) {
    return DEFAULT_METRIC_FILL_COLOR
  }

  for (const range of ranges) {
    if (range.isInRange(value)) {
      return range.color
    }
  }

  if (ranges.length > 0) {
    const firstRange = ranges[0]
    if (value < firstRange.min) {
      return firstRange.color
    }

    return ranges[ranges.length - 1].color
  }

  return DEFAULT_METRIC_FILL_COLOR
}
